import React from 'react';
import { Button, Spin, Typography } from 'antd';
import {
  CloudDownloadOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import './QRPreview.css';

const { Text } = Typography;

/**
 * Right-hand panel that displays the QR image received from the backend.
 * No QR generation logic here — just image display + download/reset actions.
 * 
 * @param {boolean} previewOnly - If true, shows only QR image with small icon, no buttons
 * @param {boolean} compact - If true, shows compact button layout (not full width)
 */
const QRPreview = ({ qrImageUrl, isLoading, onDownload, onReset, previewOnly, compact }) => {
  return (
    <div className="qr-preview-panel">
      {/* Preview only mode - show only image with small icon */}
      {previewOnly ? (
        <>
          {/* Image area */}
          <div className="qr-image-area preview-only">
            <button
              className="preview-download-icon"
              onClick={onDownload}
              disabled={!qrImageUrl || isLoading}
              title="Download QR Code"
            >
              <CloudDownloadOutlined />
            </button>
            {isLoading ? (
              <div className="qr-loading">
                <Spin size="large" />
              </div>
            ) : qrImageUrl ? (
              <img
                src={qrImageUrl}
                alt="Generated QR Code"
                className="qr-image"
                draggable={false}
              />
            ) : (
              <div className="qr-empty">
                <div className="qr-placeholder-grid" aria-hidden="true" />
                <Text className="empty-text">
                  Your QR code will appear here
                </Text>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Hint */}
          <div className="qr-hint">
            <InfoCircleOutlined className="hint-icon" />
            <Text className="hint-text">
              Always scan your QR code before use. Light background colors improve
              readability on most devices.
            </Text>
          </div>

          {/* Image area */}
          <div className="qr-image-area">
            {isLoading ? (
              <div className="qr-loading">
                <Spin size="large" />
                <Text className="loading-text">Generating your QR code…</Text>
              </div>
            ) : qrImageUrl ? (
              <img
                src={qrImageUrl}
                alt="Generated QR Code"
                className="qr-image"
                draggable={false}
              />
            ) : (
              <div className="qr-empty">
                <div className="qr-placeholder-grid" aria-hidden="true" />
                <Text className="empty-text">
                  Your QR code will appear here
                </Text>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className={`qr-actions${compact ? ' compact' : ''}`}>
            <Button
              className="download-btn"
              type="primary"
              size={compact ? 'middle' : 'large'}
              icon={<CloudDownloadOutlined />}
              block={!compact}
              disabled={!qrImageUrl || isLoading}
              onClick={onDownload}
            >
              {!compact && 'Download QR Code'}
            </Button>
            <Button
              className="reset-btn"
              size={compact ? 'middle' : 'large'}
              icon={<ReloadOutlined />}
              block={!compact}
              disabled={isLoading}
              onClick={onReset}
            >
              {!compact && 'Create New QR Code'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default QRPreview;
